import {
	ARTICLE_COMPLETE_DEPTH,
	ARTICLE_COMPLETE_DWELL_MS,
	ISSUE_COMPLETE_REPLICA,
	ISSUE_COMPLETE_RESPONSIVE,
} from '../thresholds';
import { eventLogDb, type ReadingMemoryRow } from './event-log-db';

/**
 * What makes `article_complete` and `issue_complete` fire once per device.
 * The reader calls these as reading happens; the answer says whether this
 * was the moment the rule was first met.
 *
 * Reading adds up across visits: an article read to 60% yesterday and to
 * the end today is complete today, with both days' time.
 *
 * Without a usable database the memory lasts for the page only, so a
 * completion can be reported again on a later visit. The reading side
 * already has to deduplicate, and counts readers, not rows.
 */
const fallback = new Map<string, ReadingMemoryRow>();

async function update(
	key: string,
	change: (row: ReadingMemoryRow) => ReadingMemoryRow,
): Promise<{ before: ReadingMemoryRow; after: ReadingMemoryRow }> {
	const empty: ReadingMemoryRow = { key, done: false };
	try {
		return await eventLogDb.transaction('rw', eventLogDb.readingMemory, async () => {
			const before = (await eventLogDb.readingMemory.get(key)) ?? empty;
			const after = change(before);
			await eventLogDb.readingMemory.put(after);
			return { before, after };
		});
	} catch {
		const before = fallback.get(key) ?? empty;
		const after = change(before);
		fallback.set(key, after);
		return { before, after };
	}
}

/** Adds one reading of an article. `completed` is true only the first time the rule is met. */
export async function recordArticleRead({
	issueId,
	articleId,
	dwellMs,
	depth,
}: {
	issueId: number;
	articleId: number;
	dwellMs: number;
	depth: number;
}): Promise<{ completed: boolean; dwellMs: number }> {
	const { before, after } = await update(`a:${issueId}:${articleId}`, (row) => {
		const total = (row.dwellMs ?? 0) + dwellMs;
		const deepest = Math.max(row.depth ?? 0, depth);
		return {
			...row,
			dwellMs: total,
			depth: deepest,
			done:
				row.done ||
				(deepest >= ARTICLE_COMPLETE_DEPTH && total >= ARTICLE_COMPLETE_DWELL_MS),
		};
	});
	return { completed: after.done && !before.done, dwellMs: after.dwellMs ?? 0 };
}

/**
 * Notes that a page (replica) or an article (responsive) of an issue was
 * seen. The two layouts are counted separately: pages and articles are not
 * the same unit.
 */
export async function recordUnitSeen({
	issueId,
	layout,
	unit,
	total,
}: {
	issueId: number;
	layout: 'replica' | 'responsive';
	unit: number;
	total: number;
}): Promise<{ completed: boolean; seen: number }> {
	const share =
		layout === 'replica' ? ISSUE_COMPLETE_REPLICA : ISSUE_COMPLETE_RESPONSIVE;
	const { before, after } = await update(`i:${issueId}:${layout}`, (row) => {
		const units = row.units?.includes(unit)
			? row.units
			: [...(row.units ?? []), unit];
		return {
			...row,
			units,
			done: row.done || (total > 0 && units.length / total >= share),
		};
	});
	return {
		completed: after.done && !before.done,
		seen: after.units?.length ?? 0,
	};
}

/** Test-only. */
export function resetReadingMemoryForTests(): void {
	fallback.clear();
}
